import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { DATE_FORMAT } from 'src/app/config/global.config';
import { SvgIconComponent } from 'src/app/ui/svg-icon/svg-icon.component';
import {
  IPriceCandidate,
  IPriceCandidateRow,
  IPricePolicyTrace,
  ISettingsPreferredLine,
  candidateApplyPayload,
  policyExplanation,
  settingsPreferredLine,
  toPriceCandidateRows,
  PriceSourceType,
} from '../../models/quotations.models';
import { PriceSourcePreviewService } from 'src/app/shared/file-preview-dialog/price-source-preview.service';

/**
 * Every price known for one product, on the full width.
 *
 * The compact tooltip shows two; this shows all of them, expired ones included
 * and struck through, because "why did June carry 28.45" is a question only the
 * full list can answer. It is reached from the tooltip's truncation link and
 * from nowhere else: there is no implicit threshold at which the UI decides on
 * its own to open a panel.
 *
 * Applying behaves exactly as in the tooltip — same event, same payload, same
 * refusal to apply an expired price. Two ways to apply a price that behave
 * differently is how a surface starts lying about what it did.
 */
@Component({
  selector: 'app-price-candidates-panel',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, TranslateModule, SvgIconComponent],
  templateUrl: './price-candidates-panel.component.html',
  styleUrls: ['./price-candidates-panel.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PriceCandidatesPanelComponent implements OnChanges {
  constructor(
    private translateService: TranslateService,
    private sourcePreview: PriceSourcePreviewService,
  ) {}

  @Input() candidates: IPriceCandidate[] = [];
  @Input() policyTrace: IPricePolicyTrace | null = null;
  @Input() currencySymbol = '';
  @Input() viewMode = false;
  /**
   * The order's no-price lock, passed as itself instead of folded into
   * `viewMode`: both hide the apply buttons, but one means "you are reading"
   * and the other "the server will refuse". Only the second has something to
   * explain.
   */
  @Input() noPriceLocked = false;
  /** The line's current price type, so the panel can mark which one is in use. */
  @Input() currentPriceType: PriceSourceType = 'catalog';
  /**
   * The line's current price, so "in use" names a single row.
   *
   * The type alone is a property of a source and can mark several rows; the
   * price is what tells them apart.
   */
  @Input() currentPrice: number | null | undefined = null;
  /** What the engine would apply, so "why this price" can name it. */
  @Input() selectedCandidate: IPriceCandidate | null = null;

  @Output() applyPrice = new EventEmitter<{ price: number; type: PriceSourceType }>();
  @Output() closePanel = new EventEmitter<void>();

  /** The app's one date format. An ISO string on screen is a leak, not a date. */
  readonly DATE_FORMAT = DATE_FORMAT;

  /** Read-only, whichever of the two reasons put it there. */
  get isReadOnly(): boolean {
    return this.viewMode || this.noPriceLocked;
  }

  /**
   * The rows as the template reads them: resolved once per input change.
   *
   * The panel is an overlay, so change detection runs on every mouse move
   * behind it; resolving six labels per candidate from the template would
   * re-translate the whole list on each of those passes for a text that
   * cannot have changed.
   */
  rows: IPriceCandidateRow[] = [];
  /** The translated "why this price" sentence, or null when there is none. */
  why: { key: string; params: Record<string, string> } | null = null;

  /**
   * "According to your settings: …", present only when the engine would put
   * another number on the line. Applying it covers this line alone.
   */
  settingsPreferred: ISettingsPreferredLine | null = null;

  ngOnChanges(): void {
    this.rows = toPriceCandidateRows(
      this.candidates,
      this.currencySymbol,
      this.currentPriceType,
      this.currentPrice
    );
    this.why = policyExplanation(this.policyTrace, this.selectedCandidate, (key) =>
      this.translateService.instant(key)
    );
    this.settingsPreferred = settingsPreferredLine(
      this.selectedCandidate,
      this.currentPrice,
      this.currencySymbol
    );
  }

  onApplySettingsPreferred(event: Event): void {
    event.stopPropagation();
    if (!this.settingsPreferred) return;
    const payload = candidateApplyPayload(this.settingsPreferred.candidate);
    if (payload) this.applyPrice.emit(payload);
  }

  get hasCandidates(): boolean {
    return this.rows.length > 0;
  }

  trackByRow(_index: number, row: IPriceCandidateRow): string {
    return row.key;
  }

  onApply(event: Event, row: IPriceCandidateRow): void {
    event.stopPropagation();
    const payload = candidateApplyPayload(row.candidate);
    if (payload) this.applyPrice.emit(payload);
  }

  onClose(event: Event): void {
    event.stopPropagation();
    this.closePanel.emit();
  }

  /** Show the document this price was read from — see the tooltip's note. */
  onPreviewSource(event: Event, row: IPriceCandidateRow): void {
    event.stopPropagation();
    if (!row.documentId) return;
    this.sourcePreview.open({ documentId: row.documentId, label: row.provenance });
  }
}
